import { useState } from 'react';
import { FunctionComponent } from 'react';
import { Button } from '../../tailwindcomponents';
import { saveAs } from 'file-saver';
import { CloudDownload } from '../../components/icons';
import { WorldTrip } from '../../types/worldTools';
import { FlowTextOptions, PdfDocument } from '../../utils/pdf/PdfDocument';
import { textWidth } from '../../utils/pdf/fontMetrics';
import { REPORT_MARGINS, drawReportLogo, drawReportPageChrome, getIcelandairLogo } from '../../utils/pdf/reportChrome';

type WorldToolPdfProps = {
    trip: WorldTrip
}

const styles: Record<string, FlowTextOptions> = {
    h1: { font: 'Times-Bold', size: 25, align: 'center' },
    h2: { font: 'Times-Bold', size: 18, align: 'center', marginTop: 20 },
    body: { size: 12, paddingRight: 5 },
    bold: { font: 'Times-Bold', size: 12, paddingRight: 5 },
};

// PDF content is created here, looping through the trip's tools and employees
const buildWorldToolPdf = async (trip: WorldTrip): Promise<Blob> => {
    const logo = await getIcelandairLogo();
    const doc = new PdfDocument({ margins: REPORT_MARGINS });

    drawReportLogo(doc, logo);
    doc.text(trip.general_info.name, { ...styles.h1, marginBottom: 10 });
    doc.text(trip.general_info.tail_number, { size: 18, align: 'center', marginBottom: 10 });

    const summary: [string, string][] = [
        ['Start date', trip.start_date_text],
        ['Destination', trip.world_trip_destination],
        ['End date', trip.end_date_text],
    ];
    doc.columns(summary.map(([label, value]) => ({
        width: doc.width / 3,
        render: () => {
            doc.text(label, { ...styles.bold, align: 'center' });
            doc.text(value, { ...styles.body, align: 'center' });
        },
    })));
    doc.moveDown(10);

    // Numbered list: "1 : " then the text, wrapping in its own column.
    const numberedList = (heading: string, items: string[]) => {
        doc.text(heading, { ...styles.h2, marginBottom: doc.width * 0.05 });
        items.forEach((item, index) => {
            const prefix = `${index + 1} : `;
            const prefixWidth = textWidth(prefix, 'Helvetica', 12) + 5;
            doc.columns([
                { width: prefixWidth, render: () => doc.text(prefix, styles.body) },
                { width: doc.width - prefixWidth, render: () => doc.text(item, styles.body) },
            ]);
            doc.moveDown(5);
        });
    };
    numberedList(`Tools(${trip.loaned_out_tools.length})`, trip.loaned_out_tools.map(tool => tool.description));
    numberedList(`Employees(${trip.assigned_employees.length})`, trip.assigned_employees.map(emp => emp.name));

    // Page numbers and footer fixed on each page of the pdf
    drawReportPageChrome(doc, 'Icelandair', 12);
    return doc.toBlob();
};

// This component displays a download pdf button and generates the pdf on click
const WorldToolPdf: FunctionComponent<WorldToolPdfProps> = ({ trip }) => {

    const [loading, setLoading] = useState<boolean>(false);

    // Create blob with the content, that can take a while with much data. Then saved as document
    const generatePDF = async () => {
        setLoading(true)
        try {
            const blob = await buildWorldToolPdf(trip);
            saveAs(blob, trip.general_info.name + '.pdf');
        } finally {
            setLoading(false)
        }
    }
    return (
        <Button startIcon={<CloudDownload />} variant="contained" color="primary" size="medium" onClick={() => generatePDF()}>{loading === false ? "Download PDF" : "Please wait..."}</Button>
    );
};
export default WorldToolPdf;
